'use client'
import React, {
    useEffect,
    useRef,
    useState,
    useCallback,
    forwardRef,
    useImperativeHandle,
} from "react";

export interface HorizontalScrollbarHandle {
    scrollBy: (delta: number) => void;
    scrollToStart: () => void;
    scrollToEnd: () => void;
}

interface HorizontalScrollbarProps {
    containerRef: React.RefObject<HTMLDivElement | null>;
}

export const HorizontalScrollbar = forwardRef<HorizontalScrollbarHandle, HorizontalScrollbarProps>(
    ({ containerRef }, ref) => {
        const trackRef = useRef<HTMLDivElement | null>(null);
        const dragStartX = useRef(0);
        const dragStartLeft = useRef(0);
        const [thumbWidth, setThumbWidth] = useState(0);
        const [thumbLeft, setThumbLeft] = useState(0);
        const [dragging, setDragging] = useState(false);
        const [hideThumb, setHideThumb] = useState(false);

        // --- Update thumb position ---
        const updateThumb = useCallback(() => {
            const container = containerRef.current;
            const track = trackRef.current;
            if (!container || !track) return;

            const trackWidth = track.clientWidth;
            const contentWidth = container.scrollWidth;
            const containerWidth = container.clientWidth;

            const width = Math.max((containerWidth / contentWidth) * trackWidth, 32);
            const maxThumb = trackWidth - width;
            const progress = container.scrollLeft / (contentWidth - containerWidth);

            setHideThumb(width >= trackWidth);
            setThumbWidth(width);
            setThumbLeft(progress * maxThumb);
        }, [containerRef]);

        useEffect(() => {
            updateThumb();
            const container = containerRef.current;
            if (!container) return;

            container.addEventListener("scroll", updateThumb);
            window.addEventListener("resize", updateThumb);

            return () => {
                container.removeEventListener("scroll", updateThumb);
                window.removeEventListener("resize", updateThumb);
            };
        }, [containerRef, updateThumb]);

        // --- Drag thumb ---
        useEffect(() => {
            if (!dragging || !containerRef.current || !trackRef.current) return;

            const container = containerRef.current;
            const trackWidth = trackRef.current.clientWidth;
            const maxThumb = trackWidth - thumbWidth;

            const onMove = (clientX: number) => {
                const dx = clientX - dragStartX.current;
                const newLeft = Math.min(Math.max(dragStartLeft.current + dx, 0), maxThumb);
                const scrollableWidth = container.scrollWidth - container.clientWidth;
                container.scrollLeft = (newLeft / maxThumb) * scrollableWidth;
                setThumbLeft(newLeft);
            };

            const onMouseMove = (e: MouseEvent) => onMove(e.clientX);
            const onTouchMove = (e: TouchEvent) => onMove(e.touches[0].clientX);
            const onEnd = () => setDragging(false);

            document.addEventListener("mousemove", onMouseMove);
            document.addEventListener("mouseup", onEnd);
            document.addEventListener("touchmove", onTouchMove);
            document.addEventListener("touchend", onEnd);

            return () => {
                document.removeEventListener("mousemove", onMouseMove);
                document.removeEventListener("mouseup", onEnd);
                document.removeEventListener("touchmove", onTouchMove);
                document.removeEventListener("touchend", onEnd);
            };
        }, [dragging, containerRef, thumbWidth]);

        const startDrag = (clientX: number) => {
            dragStartX.current = clientX;
            dragStartLeft.current = thumbLeft;
            setDragging(true);
        };

        // --- Click-to-jump ---
        const onTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
            const container = containerRef.current;
            const track = trackRef.current;
            if (!container || !track) return;

            const rect = track.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const maxThumb = track.clientWidth - thumbWidth;
            const newLeft = Math.min(Math.max(clickX - thumbWidth / 2, 0), maxThumb);

            const scrollableWidth = container.scrollWidth - container.clientWidth;
            container.scrollLeft = (newLeft / maxThumb) * scrollableWidth;
        };

        // --- Expose methods ---
        useImperativeHandle(ref, () => ({
            scrollBy: (delta: number) => {
                const container = containerRef.current;
                if (!container) return;
                container.scrollBy({ left: delta, behavior: "smooth" });
            },
            scrollToStart: () => {
                const container = containerRef.current;
                if (!container) return;
                container.scrollTo({ left: 0, behavior: "smooth" });
            },
            scrollToEnd: () => {
                const container = containerRef.current;
                if (!container) return;
                container.scrollTo({ left: container.scrollWidth, behavior: "smooth" });
            },
        }));

        if (hideThumb) return null;

        return (
            <div
                ref={trackRef}
                onClick={onTrackClick}
                className="relative flex-1 h-[5px] mt-[30px] bg-brand-tertiary rounded-full cursor-pointer select-none lg:max-w-[400px] lg:mx-auto"
            >
                <div
                    onMouseDown={(e) => startDrag(e.clientX)}
                    onTouchStart={(e) => startDrag(e.touches[0].clientX)}
                    className="absolute h-full bg-brand-quaternary-lighten rounded-full active:cursor-grabbing"
                    style={{
                        width: thumbWidth,
                        transform: `translateX(${thumbLeft}px)`,
                    }}
                />
            </div>
        );
    }
);

HorizontalScrollbar.displayName = "HorizontalScrollbar";